import React, { useEffect, useMemo, useState } from "react";
import { Folder, FolderOpen, Music, Search, Plus, ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * TrackBrowser — Serato-style split browser:
 *   LEFT  crate tree (All / Recent / by-genre)
 *   RIGHT track table with columns: song · artist · bpm · key · genre
 * Double-click a row to load into Deck A; right-click/alt-click loads B.
 *
 * BPM and Key are synthesized (stable hash) until we integrate a real
 * analyzer — good enough for the visual layout.
 *
 * Catalog source: the host's station library + (optionally) Spotify
 * search results filtered to provider==='spotify'. iTunes catalog
 * suggestions were retired — use the Spotify panel for full-length
 * track lookups.
 */

const CRATES = [
  { id: "all",      label: "All Tracks",    genre: null },
  { id: "recent",   label: "Recently Added", genre: null, sort: "recent" },
  { id: "hiphop",   label: "Hip Hop & RnB",  genre: "hip hop" },
  { id: "rock",     label: "Rock n Roll",    genre: "rock" },
  { id: "electro",  label: "Electronic",     genre: "electronic" },
  { id: "country",  label: "Country",        genre: "country" },
  { id: "latin",    label: "Latin",          genre: "latin" },
  { id: "gospel",   label: "Gospel",         genre: "gospel" },
  { id: "jazz",     label: "Jazz",           genre: "jazz" },
  { id: "party",    label: "Party Tunes",    genre: "party" },
];

function hashNum(s, min, max) {
  let h = 0;
  const str = (s || "").toString();
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h) + str.charCodeAt(i);
  return min + (Math.abs(h) % (max - min + 1));
}
const KEYS = ["1A","2A","3A","4A","5A","6A","7A","8A","9A","10A","11A","12A","1B","2B","3B","4B","5B","6B","7B","8B","9B","10B","11B","12B"];
function inferKey(id) { return KEYS[hashNum(id || "?", 0, KEYS.length - 1)]; }
function inferBpm(t) {
  // Prefer explicit bpm field if backend later provides it
  if (t && typeof t.bpm === "number" && t.bpm > 0) return t.bpm;
  const base = hashNum(t?.id || t?.title || "?", 70, 165);
  return base;
}
// Stamp a stable inferred BPM onto the track BEFORE we hand it to the deck
// so the SYNC button has a useful fallback while `useBpmAnalyzer` is still
// fetching+decoding the source. Once the real analyzer returns, the deck
// switches to that more accurate value automatically.
function withInferredBpm(t) {
  if (!t) return t;
  if (typeof t.bpm === "number" && t.bpm > 0) return t;
  return { ...t, bpm: inferBpm(t), _bpm_inferred: true };
}
function genreOf(t) {
  return t?.genre || t?.category || t?.station_slug?.replace(/-/g, " ") || "Various";
}

export default function TrackBrowser({ slug, tracks, onReload, onLoadA, onLoadB, onEnqueue, spotifyAuth, onLoadSpotifyA, onLoadSpotifyB }) {
  const [crate, setCrate] = useState("all");
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expandedCrates, setExpandedCrates] = useState(true);
  // Collapse toggle for the whole list — DJs work the deck up top, the
  // list lives below. Default open so first-time users see tracks; once
  // collapsed they get more breathing room for the bottom panels.
  const [browserOpen, setBrowserOpen] = useState(true);
  // Playlist import state — paste a YouTube/SoundCloud playlist URL and
  // we resolve all tracks via yt-dlp on the backend, preview the list,
  // then bulk-add to the station library in one POST. Way faster than
  // searching one track at a time when setting up a new show.
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { source, playlist_title, tracks, total }

  const handleResolvePlaylist = async () => {
    const url = importUrl.trim();
    if (!url) { toast.error("Paste a playlist URL"); return; }
    setImporting(true);
    setImportPreview(null);
    try {
      const { data } = await api.post("/radio/import-playlist", { url, limit: 50 });
      if (!data?.ok || !data.tracks?.length) {
        toast.error("Couldn't find tracks in that playlist");
        return;
      }
      setImportPreview(data);
      toast.success(`Found ${data.total} tracks in "${data.playlist_title}"`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.tracks?.length) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/radio/stations/${slug}/tracks/bulk`, {
        tracks: importPreview.tracks,
      });
      toast.success(`Added ${data.added} tracks${data.skipped_dupes ? ` · ${data.skipped_dupes} duplicates skipped` : ""}`);
      onReload?.();
      setImportOpen(false);
      setImportUrl("");
      setImportPreview(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const spotifyLoggedIn = !!spotifyAuth?.loggedIn;

  // Live search — multi-source streamable catalog (YouTube + SoundCloud).
  // Both source types serve audio that flows through a regular <audio>
  // element, so the deck routes them through the EQ / Announcer FX /
  // Auto-Fix / recorder, AND listeners hear them in the in-app player.
  // Spotify is intentionally excluded here because Web Playback SDK
  // serves DRM-encrypted bytes that browsers refuse to expose to
  // JavaScript — those tracks remain monitor-only via the SpotifyPanel.
  // When the user has a Spotify session, we ALSO query Spotify
  // separately and append the results below the streamable hits so
  // they can still load Spotify tracks (with a clear "monitor-only"
  // badge) alongside the streamable ones.
  useEffect(() => {
    if (q.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        // 1) Streamable sources via /api/radio/search-stream
        const streamReq = api.get(`/radio/search-stream?q=${encodeURIComponent(q)}&limit=12`);
        // 2) Spotify catalog (parallel, only if logged in)
        const spotifyReq = (spotifyLoggedIn && spotifyAuth?.getFreshAccessToken)
          ? (async () => {
              try {
                const tok = await spotifyAuth.getFreshAccessToken();
                if (!tok) return [];
                const res = await fetch(
                  `https://api.spotify.com/v1/search?type=track&limit=6&q=${encodeURIComponent(q)}`,
                  { headers: { Authorization: `Bearer ${tok}` } },
                );
                if (!res.ok) return [];
                const data = await res.json();
                return (data?.tracks?.items || []).map((it) => ({
                  id: `spotify:${it.id}`,
                  provider: "spotify",
                  title: it.name,
                  artist: (it.artists || []).map((a) => a.name).join(", "),
                  album: it.album?.name || "",
                  cover_url: it.album?.images?.[1]?.url || it.album?.images?.[0]?.url || "",
                  duration_sec: Math.round((it.duration_ms || 0) / 1000),
                  _sp: it,
                }));
              } catch { return []; }
            })()
          : Promise.resolve([]);

        const [streamRes, spotifyRes] = await Promise.all([streamReq, spotifyReq]);
        const streamHits = streamRes?.data || [];
        // Concatenate: streamable first (deck-playable + listener-audible),
        // Spotify last (monitor-only) so the default A/B click lands on
        // a track that listeners actually hear.
        setSearchResults([...streamHits, ...spotifyRes]);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, spotifyLoggedIn, spotifyAuth]);

  const filtered = useMemo(() => {
    let out = tracks || [];
    const c = CRATES.find((x) => x.id === crate);
    if (c?.genre) {
      out = out.filter((t) => {
        const tag = (genreOf(t) + " " + (t.title || "") + " " + (t.artist || "")).toLowerCase();
        return tag.includes(c.genre);
      });
    }
    if (c?.sort === "recent") {
      out = [...out].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    const query = q.trim().toLowerCase();
    if (query) {
      out = out.filter((t) =>
        (t.title || "").toLowerCase().includes(query) ||
        (t.artist || "").toLowerCase().includes(query)
      );
    }
    return out;
  }, [tracks, crate, q]);

  // A/B click handler — routes to the correct deck-load path based on
  // which catalog the hit came from. YouTube + SoundCloud go through
  // the existing `addAndLoad` path which resolves a playable URL,
  // creates a station track row, and loads it into the deck. Spotify
  // hits route through the studio's sendSpotifyToDeck (monitor-only).
  const handleHitToDeck = async (hit, side) => {
    if (hit.provider === "spotify") {
      side === "a" ? onLoadSpotifyA?.(hit._sp) : onLoadSpotifyB?.(hit._sp);
      setQ("");
      return;
    }
    // For YouTube hits we resolve the direct audio CDN URL up-front so
    // the deck's <audio> element can stream it through Web Audio.
    let source_url = hit.source_url || hit.preview_url || "";
    let source_type = hit.provider === "youtube" ? "youtube" : "soundcloud";
    if (hit.provider === "youtube") {
      try {
        const vid = (hit.id || "").split(":")[1];
        if (vid) {
          const { data } = await api.get(`/radio/resolve-youtube/full?video_id=${encodeURIComponent(vid)}`);
          if (data?.ok && data.url) source_url = data.url;
        }
      } catch {
        // Fall back to the watch URL — the deck will still try, and
        // surface a clear error if browsers can't play it.
      }
    } else if (hit.provider === "soundcloud" && !source_url) {
      // SoundCloud preview wasn't pre-resolved during search — fetch it now.
      try {
        const { data } = await api.get(`/music/track/${encodeURIComponent(hit.id)}`);
        source_url = data?.preview_url || "";
      } catch { /* ignore */ }
    }
    try {
      const { data: t } = await api.post(`/radio/stations/${slug}/tracks`, {
        title: hit.title,
        artist: hit.artist,
        album: hit.album || "",
        cover_url: hit.cover_url,
        source_url,
        source_type,
        duration_sec: hit.duration_sec || 0,
      });
      toast.success(`+ ${hit.title}`);
      onReload?.();
      side === "a" ? onLoadA(t) : onLoadB(t);
      setQ("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleRowClick = (t, e) => {
    if (e.altKey || e.metaKey || e.ctrlKey) onLoadB(t);
    else onLoadA(t);
  };
  const handleRowDouble = (t) => onLoadA(t);

  return (
    <section
      data-testid="serato-browser"
      className={`grid grid-cols-[160px_minmax(0,1fr)] md:grid-cols-[180px_minmax(0,1fr)] border-t border-white/10 bg-[#050609] text-white overflow-hidden transition-all`}
      style={{ minHeight: browserOpen ? 260 : 44 }}
    >
      {/* Crates tree */}
      <aside className={`border-r border-white/10 bg-[#0A0D14] py-2 overflow-y-auto text-[11px] ${browserOpen ? "" : "hidden md:block"}`}>
        <button
          onClick={() => setExpandedCrates((v) => !v)}
          className="w-full flex items-center gap-1 px-2 py-1 text-white/70 hover:text-white"
        >
          {expandedCrates ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {expandedCrates ? <FolderOpen className="w-3 h-3 text-tv-accent" /> : <Folder className="w-3 h-3 text-tv-accent" />}
          <span className="font-bold">Crates</span>
        </button>
        {expandedCrates && (
          <div className="pl-3">
            {CRATES.map((c) => (
              <button
                key={c.id}
                data-testid={`serato-crate-${c.id}`}
                onClick={() => setCrate(c.id)}
                className={`w-full text-left flex items-center gap-1.5 px-2 py-1 rounded ${crate === c.id ? "bg-tv-accent/20 text-tv-accent" : "text-white/70 hover:bg-white/5"}`}
              >
                <Music className="w-3 h-3 opacity-70" />
                <span className="truncate">{c.label}</span>
              </button>
            ))}
          </div>
        )}
      </aside>

      {/* Track table */}
      <div className="flex flex-col min-h-0 min-w-0">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-[#0A0D14]">
          <button
            data-testid="serato-browser-toggle"
            onClick={() => setBrowserOpen((v) => !v)}
            title={browserOpen ? "Collapse track list" : "Expand track list"}
            className="text-white/55 hover:text-white p-0.5 rounded"
          >
            {browserOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <Search className="w-3 h-3 text-white/40" />
          <input
            data-testid="serato-browser-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!browserOpen) setBrowserOpen(true); }}
            placeholder="Search YouTube + SoundCloud (full tracks · listeners hear them)…"
            className="flex-1 bg-transparent text-white text-[12px] outline-none placeholder:text-white/30"
          />
          <button
            type="button"
            onClick={() => { setImportOpen((v) => !v); if (!browserOpen) setBrowserOpen(true); }}
            data-testid="serato-import-playlist-btn"
            title="Paste a YouTube or SoundCloud playlist URL — bulk-import all tracks"
            className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded border transition ${
              importOpen
                ? "bg-tv-accent text-black border-tv-accent"
                : "bg-white/5 border-white/15 text-white/70 hover:text-white hover:border-white/30"
            }`}
          >
            + Playlist
          </button>
          <div className="text-[10px] text-white/40 tabular-nums">{filtered.length} tracks</div>
        </div>

        {/* Playlist import inline panel */}
        {importOpen && (
          <div data-testid="serato-import-panel" className="border-b border-white/10 bg-black/40 px-3 py-2 space-y-2">
            <div className="flex items-center gap-2">
              <input
                data-testid="serato-import-url"
                type="url"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="Paste YouTube or SoundCloud playlist URL…"
                disabled={importing}
                className="flex-1 bg-white/5 border border-white/15 rounded px-2 py-1 text-[11px] text-white outline-none focus:border-tv-accent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={handleResolvePlaylist}
                disabled={importing || !importUrl.trim()}
                data-testid="serato-import-resolve"
                className="bg-tv-accent text-black text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded disabled:opacity-40"
              >
                {importing && !importPreview ? "…" : "Find"}
              </button>
              <button
                type="button"
                onClick={() => { setImportOpen(false); setImportUrl(""); setImportPreview(null); }}
                className="text-white/50 hover:text-white text-[10px] font-black uppercase tracking-wider px-2 py-1.5"
              >
                Close
              </button>
            </div>
            {importPreview && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[10px]">
                  <div className="text-white/70 truncate">
                    <span className="font-black uppercase tracking-wider text-tv-accent">{importPreview.source}</span> · &ldquo;{importPreview.playlist_title}&rdquo; · {importPreview.total} tracks
                  </div>
                  <button
                    type="button"
                    onClick={handleConfirmImport}
                    disabled={importing}
                    data-testid="serato-import-confirm"
                    className="bg-emerald-500 hover:bg-emerald-400 text-black text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded disabled:opacity-40"
                  >
                    {importing ? "Adding…" : `Add all ${importPreview.total}`}
                  </button>
                </div>
                <div className="max-h-32 overflow-y-auto bg-black/40 rounded">
                  {importPreview.tracks.slice(0, 10).map((t, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 text-[10px] border-b border-white/5 last:border-b-0">
                      <span className="text-white/30 tabular-nums w-5">{i + 1}</span>
                      {t.cover_url ? (
                        <div className="w-5 h-5 rounded shrink-0" style={{ backgroundImage: `url(${t.cover_url})`, backgroundSize: "cover" }} />
                      ) : <div className="w-5 h-5 rounded bg-white/10 shrink-0" />}
                      <span className="font-bold truncate flex-1">{t.title}</span>
                      <span className="text-white/50 truncate w-32">{t.artist}</span>
                    </div>
                  ))}
                  {importPreview.tracks.length > 10 && (
                    <div className="px-2 py-1 text-[10px] text-white/40 italic">…and {importPreview.tracks.length - 10} more</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Multi-source streamable catalog suggestion strip */}
        {browserOpen && q.trim().length >= 2 && searchResults.length > 0 && (
          <div className="border-b border-white/10 bg-black/40 px-3 py-2">
            <div className="text-[9px] uppercase tracking-wider text-white/40 font-black mb-1 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400" /> YouTube</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-400" /> SoundCloud</span>
              {spotifyLoggedIn && (
                <span className="inline-flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" /> Spotify · monitor only</span>
              )}
              <span className="text-white/30">· tap A or B to load</span>
            </div>
            <div className="flex gap-2 overflow-x-auto hide-scrollbar">
              {searchResults.slice(0, 12).map((hit, i) => {
                const dot = hit.provider === "youtube"
                  ? "bg-rose-400"
                  : hit.provider === "soundcloud"
                    ? "bg-orange-400"
                    : "bg-emerald-400";
                const badge = hit.provider === "youtube"
                  ? "YT"
                  : hit.provider === "soundcloud"
                    ? "SC"
                    : "SP";
                return (
                  <div key={`${hit.provider}-${hit.id || i}`} className="shrink-0 w-60 flex items-center gap-2 bg-white/5 rounded p-1.5">
                    <div className="relative w-9 h-9 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${hit.cover_url})`, backgroundSize: "cover" }}>
                      <span className={`absolute -top-1 -right-1 ${dot} text-black text-[7px] font-black px-1 py-px rounded`}>{badge}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold line-clamp-1">{hit.title}</div>
                      <div className="text-white/50 text-[10px] line-clamp-1">{hit.artist}</div>
                    </div>
                    <button
                      data-testid={`serato-browser-add-a-${i}`}
                      onClick={() => handleHitToDeck(hit, "a")}
                      title="Load to Deck A"
                      className="bg-sky-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded"
                    >A</button>
                    <button
                      data-testid={`serato-browser-add-b-${i}`}
                      onClick={() => handleHitToDeck(hit, "b")}
                      title="Load to Deck B"
                      className="bg-rose-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded"
                    >B</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty-search hints */}
        {browserOpen && q.trim().length >= 2 && !spotifyLoggedIn && !searching && searchResults.length === 0 && (
          <div className="border-b border-white/10 bg-black/40 px-3 py-2 text-[10px] text-white/40">
            No matches for &ldquo;{q}&rdquo; on YouTube or SoundCloud. (Connect Spotify above to also search the Spotify catalog as a monitor-only fallback.)
          </div>
        )}
        {browserOpen && q.trim().length >= 2 && spotifyLoggedIn && !searching && searchResults.length === 0 && (
          <div className="border-b border-white/10 bg-black/40 px-3 py-2 text-[10px] text-white/40">
            No matches for &ldquo;{q}&rdquo;.
          </div>
        )}

        {browserOpen && (
          <>
            {/* Column headers */}
            <div className="grid grid-cols-[28px_1fr_1fr_60px_50px_120px_60px] gap-2 px-3 py-1.5 text-[9px] uppercase tracking-wider font-black text-white/40 border-b border-white/10 bg-[#0A0D14]">
              <span>#</span>
              <span>Song</span>
              <span>Artist</span>
              <span className="text-right">BPM</span>
              <span>Key</span>
              <span>Genre</span>
              <span className="text-right">Load</span>
            </div>

            {/* Rows — capped height + scroll so the panels below are reachable */}
            <div data-testid="serato-browser-rows" className="overflow-y-auto" style={{ maxHeight: 360 }}>
              {filtered.length === 0 && !searching && (
                <div className="py-10 text-center text-white/40 text-[11px]">
                  {q ? "No library matches. Try typing more — Spotify catalog suggestions appear above." : "Crate is empty."}
                </div>
              )}
              {filtered.map((t, i) => {
                const bpm = inferBpm(t);
                const key = inferKey(t.id);
                return (
                  <div
                    key={t.id}
                    data-testid={`serato-browser-row-${t.id}`}
                    onClick={(e) => handleRowClick(t, e)}
                    onDoubleClick={() => handleRowDouble(t)}
                    className="grid grid-cols-[28px_1fr_1fr_60px_50px_120px_60px] gap-2 px-3 py-1 text-[11px] hover:bg-tv-accent/10 cursor-pointer border-b border-white/[0.03]"
                    title="Click = Deck A · Alt/Cmd+Click = Deck B · Double-click = Deck A + Play"
                  >
                    <div className="text-white/40 font-mono">{i + 1}</div>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="w-5 h-5 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${t.cover_url})`, backgroundSize: "cover" }} />
                      <span className="text-white truncate">{t.title}</span>
                    </div>
                    <div className="text-white/70 truncate">{t.artist}</div>
                    <div className="text-right font-mono text-white/80 tabular-nums">{bpm}</div>
                    <div className="font-mono text-emerald-300">{key}</div>
                    <div className="text-white/60 truncate capitalize">{genreOf(t)}</div>
                    <div className="flex gap-1 justify-end">
                      <button
                        data-testid={`serato-browser-load-a-${t.id}`}
                        onClick={(e) => { e.stopPropagation(); onLoadA(withInferredBpm({ ...t, bpm })); }}
                        className="bg-sky-500/25 hover:bg-sky-500 text-sky-100 hover:text-black text-[9px] font-black px-1.5 rounded"
                      >A</button>
                      <button
                        data-testid={`serato-browser-load-b-${t.id}`}
                        onClick={(e) => { e.stopPropagation(); onLoadB(withInferredBpm({ ...t, bpm })); }}
                        className="bg-rose-500/25 hover:bg-rose-500 text-rose-100 hover:text-black text-[9px] font-black px-1.5 rounded"
                      >B</button>
                      {onEnqueue && (
                        <button
                          data-testid={`serato-browser-enqueue-${t.id}`}
                          onClick={(e) => { e.stopPropagation(); onEnqueue(withInferredBpm({ ...t, bpm })); }}
                          title="Add to SetList queue"
                          className="bg-emerald-500/25 hover:bg-emerald-500 text-emerald-100 hover:text-black text-[10px] font-black px-1.5 rounded leading-none"
                        >+</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
